import React, { useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import Pagination from "react-js-pagination";
import moment from "moment";
import AliianceManagerLayout from "../../../components/layouts/AliianceManagerLayout";
import Breadcrumb from "../../../components/common/Breadcrumb";
import AdminPageCard from "../../../components/common/AdminPageCard/AdminPageCard";
import AdminPageHeader from "../../../components/AdminPageHeader";
import AdminTable from "../../../components/common/AdminTable/AdminTable";
import VendorExecutiveServices from "../../../services/API/VendorExecutive";
import FieldExecutiveServices from "../../../services/API/FieldExecutive";
import SideFilter from "../../../Containers/Vendor/FieldExecutive/SideFilter";
import { useVendors } from "../../../hooks/useVendors";
import StatusChangeModal from "./StatusChangeModal";

const limit = 20;

const initialFilters = {
  name: "",
  mobile_no: "",
  email: "",
  executive_name: "",
  is_enabled: "",
  vendor: null,
  dueDateRange: [
    { startDate: new Date(), endDate: new Date(), key: "selection" },
  ],
  dateSubmit: false,
};

// Per-type settings for the Alliance Manager executive / field executive lists
const CONFIG = {
  executive: {
    title: "Executives Lists",
    addTitle: "Add Executive",
    addPath: "/alliance-manager/executive/create",
    editPath: "/alliance-manager/vendors/executive/edit/",
    spocType: "Executive",
  },
  fieldExecutive: {
    title: "Field Executives Lists",
    addTitle: "Add Field Executive",
    addPath: "/alliance-manager/field-executives/create",
    editPath: "/alliance-manager/vendor/field-executives/edit/",
    spocType: "Field Executive",
  },
};

const VendorUserList = ({ type }) => {
  const config = CONFIG[type];
  const isFE = type === "fieldExecutive";
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(initialFilters);
  const [showFilter, setShowFilter] = useState(false);
  const [showDuedateRange, setShowDuedateRange] = useState(false);
  const [statusItem, setStatusItem] = useState(null); // { row, rowNo } for activate / deactivate
  const { vendors } = useVendors();
  // Executive / FE rows carry the owning vendor's name, or its id in created_by
  const getVendorName = (row) =>
    row?.vendor_name ||
    vendors?.find((item) => item?.id == row?.created_by)?.name ||
    "--";

  // Only send filters that are set; with none, Alliance Manager gets all records of all vendors
  const queryParams = { limit, page };
  ["name", "mobile_no", "email", "is_enabled"].forEach((key) => {
    if (filters[key] !== "") queryParams[key] = filters[key];
  });
  if (isFE && filters.executive_name)
    queryParams.executive_name = filters.executive_name;
  if (filters.dateSubmit) {
    queryParams.from_created_on = filters.from_created_on;
    queryParams.to_created_on = filters.to_created_on;
  }
  if (filters.vendor) queryParams.vendorIds = [filters.vendor.id];
  const hasFilters = Object.keys(queryParams).length > 2;

  const clearFilters = () => {
    setFilters(initialFilters);
    setShowDuedateRange(false);
    setPage(1);
  };

  // isFetching (not isLoading) so the loader also shows on page / filter changes
  const { data: list, isFetching, refetch } = useQuery(
    [isFE ? "/api/field_executive" : "/api/executive", queryParams],
    isFE ? FieldExecutiveServices.getFElist : VendorExecutiveServices.getRQExecutives,
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );
  // API returns either a paginated object ({ data, total, ... }) or a plain array of all rows;
  // a plain array is paginated on the client so every record is still listed
  const body = list?.data;
  const isPlainList = Array.isArray(body);
  const allRows = (isPlainList ? body : body?.data) || [];
  const rows = isPlainList
    ? allRows.slice(limit * (page - 1), limit * page)
    : allRows;
  const total = isPlainList ? allRows.length : body?.total;

  const breadcrumbs = [
    { title: "Home", link: "/alliance-manager/dashboard", isActive: true },
    { title: config.title, isActive: false },
  ];

  const columns = [
    {
      title: "S. No.",
      value: (column, index) => limit * (page - 1) + index + 1,
      thProps: { width: "50px" },
    },
    {
      title: "Name",
      value: (column) => (
        <Link
          to={`/alliance-manager/vendors/spoc/${column?.id}`}
          state={{ spocType: config.spocType }}
        >
          {column?.name}
        </Link>
      ),
    },
    {
      title: "Mobile Number",
      value: (column) => column?.mobile_no?.replace("+91", ""),
    },
    { title: "Email ID", value: (column) => column?.email },
    { title: "Vendor Name", value: (column) => getVendorName(column) },
    ...(isFE
      ? [{ title: "Executive Name", value: (column) => column?.executive_name }]
      : []),
    {
      title: "Created On",
      value: (column) => {
        const createdOn = column?.created_on || column?.created_at;
        return createdOn ? moment(createdOn).format("DD-MM-YY hh:mm A") : "--";
      },
    },
    {
      title: "Actions",
      value: (column, index) => (
        <>
          <i
            className="fa fa-edit fa-fw cursor-pointer"
            title="Edit"
            onClick={() => navigate(`${config.editPath}${column?.id}`)}
          ></i>
          <i
            className={`fa fa-circle fa-fw cursor-pointer ${
              column?.is_enabled == 1 ? "txt-success" : "txt-fail"
            }`}
            title={column?.is_enabled == 1 ? "Deactivate" : "Activate"}
            onClick={() =>
              setStatusItem({ row: column, rowNo: limit * (page - 1) + index + 1 })
            }
          ></i>
        </>
      ),
      thProps: { width: "70px" },
    },
  ];

  return (
    <AliianceManagerLayout>
      <div id="main" role="main">
        <div id="content">
          <StatusChangeModal
            item={statusItem}
            isFE={isFE}
            onClose={() => setStatusItem(null)}
            onSuccess={refetch}
          />
          {/* Mounted only while open so it starts from the applied filters */}
          {showFilter && (
            <SideFilter
              show={showFilter}
              onHide={() => setShowFilter(false)}
              filters={filters}
              setFilters={setFilters}
              setPage={setPage}
              initial={initialFilters}
              showDuedateRange={showDuedateRange}
              setShowDuedateRange={setShowDuedateRange}
              showExecutiveName={isFE}
              vendorOptions={vendors}
            />
          )}
          <Breadcrumb items={breadcrumbs} />
          <AdminPageCard>
            <div className="d-flex justify-content-between align-items-center">
              <AdminPageHeader title={config.title} />
              <div className="d-flex gap-2 align-items-center pb-2">
                {hasFilters && (
                  <button
                    type="button"
                    className="button_new mt-0"
                    onClick={clearFilters}
                  >
                    Clear Filters
                  </button>
                )}
                <button
                  type="button"
                  className="button_new mt-0"
                  title="Show Filters"
                  onClick={() => setShowFilter(true)}
                >
                  <i className="fa fa-filter fa-fw"></i>
                </button>
                <button
                  type="button"
                  className="button_new mt-0"
                  title={config.addTitle}
                  onClick={() => navigate(config.addPath)}
                >
                  <i className="fa fa-plus fa-fw"></i>
                  {config.addTitle}
                </button>
              </div>
            </div>
            <AdminTable
              columns={columns}
              data={rows}
              isDataLoading={isFetching}
            />
            {total > limit && (
              <Pagination
                activePage={page}
                itemsCountPerPage={limit}
                totalItemsCount={total}
                pageRangeDisplayed={5}
                onChange={(number) => setPage(number)}
              />
            )}
          </AdminPageCard>
        </div>
      </div>
    </AliianceManagerLayout>
  );
};

export default VendorUserList;
